/**
 * DEV-only permanent progression helpers.
 * Never used by player-facing settlement / combat paths.
 * Caps match VANGUARD_FINAL_SPEC / M5 schema (no invented economy).
 */

import { syncGeneralPointsFromLevel } from '../domain/generalTalents';
import {
  CLASS_LEVEL_CAP,
  CLASS_LEVEL_EARNED_POINTS_CAP,
  GENERAL_LEVEL_CAP,
  GENERAL_LEVEL_EARNED_POINTS_CAP,
  SAVE_SCHEMA_VERSION,
  createDefaultPermanentSave,
  type ClassProgress,
  type PermanentSave,
  type SavedGame,
} from './schema';
import { createSaveStore } from './saveStore';

export interface DevProgressSnapshot {
  generalLevel: number;
  generalEarnedPoints: number;
  generalSpentPoints: number;
  generalUnspentPoints: number;
  vanguardLevel: number;
  earnedPoints: number;
  spentPoints: number;
  unspentPoints: number;
}

function syncEarnedFromLevel(cls: ClassProgress): ClassProgress {
  const fromLevel = Math.min(
    CLASS_LEVEL_EARNED_POINTS_CAP,
    Math.max(0, cls.level - 1),
  );
  return {
    ...cls,
    earnedPoints: Math.max(cls.earnedPoints, fromLevel),
    talentRanks: cls.talentRanks ?? {},
  };
}

function withVanguard(
  permanent: PermanentSave,
  next: ClassProgress,
): PermanentSave {
  return {
    ...permanent,
    classes: {
      ...permanent.classes,
      vanguard: {
        ...next,
        talentRanks: next.talentRanks ?? {},
      },
    },
  };
}

/** Load permanent or default; preserve active run. */
export function loadDevPermanent(): {
  permanent: PermanentSave;
  previous: SavedGame | null;
} {
  const previous = createSaveStore().load();
  return {
    permanent: previous?.permanent ?? createDefaultPermanentSave(),
    previous,
  };
}

export function saveDevPermanent(
  permanent: PermanentSave,
  previous: SavedGame | null,
): void {
  createSaveStore().save({
    version: previous?.version ?? SAVE_SCHEMA_VERSION,
    savedAt: Date.now(),
    rngState: previous?.rngState ?? 0,
    permanent,
    run: previous?.run ?? null,
  });
}

export function snapshotDevProgress(permanent: PermanentSave): DevProgressSnapshot {
  const cls = permanent.classes.vanguard;
  const gen = permanent.general;
  return {
    generalLevel: gen.level,
    generalEarnedPoints: gen.earnedPoints,
    generalSpentPoints: gen.spentPoints,
    generalUnspentPoints: Math.max(0, gen.earnedPoints - gen.spentPoints),
    vanguardLevel: cls.level,
    earnedPoints: cls.earnedPoints,
    spentPoints: cls.spentPoints,
    unspentPoints: Math.max(0, cls.earnedPoints - cls.spentPoints),
  };
}

/** Grant unspent class talent points without inventing achievement grants. */
export function grantVanguardTalentPoints(
  permanent: PermanentSave,
  amount: number,
): PermanentSave {
  if (amount <= 0) return permanent;
  const cls = permanent.classes.vanguard;
  const nextEarned = Math.min(
    CLASS_LEVEL_EARNED_POINTS_CAP,
    cls.earnedPoints + amount,
  );
  // Keep level at least earned+1 so UI stays coherent with point source.
  const nextLevel = Math.max(cls.level, Math.min(CLASS_LEVEL_CAP, nextEarned + 1));
  return withVanguard(permanent, {
    ...cls,
    earnedPoints: nextEarned,
    level: nextLevel,
  });
}

/** Raise Vanguard class level; earned talent points follow level−1 (capped). */
export function grantVanguardLevels(
  permanent: PermanentSave,
  levels: number,
): PermanentSave {
  if (levels <= 0) return permanent;
  const cls = permanent.classes.vanguard;
  const nextLevel = Math.min(CLASS_LEVEL_CAP, cls.level + levels);
  return withVanguard(
    permanent,
    syncEarnedFromLevel({
      ...cls,
      level: nextLevel,
    }),
  );
}

/** Raise shared General level; talent points follow level−1 (capped at 49). */
export function grantGeneralLevels(
  permanent: PermanentSave,
  levels: number,
): PermanentSave {
  if (levels <= 0) return permanent;
  const nextLevel = Math.min(
    GENERAL_LEVEL_CAP,
    permanent.general.level + levels,
  );
  return {
    ...permanent,
    general: syncGeneralPointsFromLevel({
      ...permanent.general,
      level: nextLevel,
    }),
  };
}

/** Grant unspent General talent points (DEV). Does not invent milestone grants. */
export function grantGeneralTalentPoints(
  permanent: PermanentSave,
  amount: number,
): PermanentSave {
  if (amount <= 0) return permanent;
  const nextEarned = Math.min(
    GENERAL_LEVEL_EARNED_POINTS_CAP,
    permanent.general.earnedPoints + amount,
  );
  const nextLevel = Math.max(
    permanent.general.level,
    Math.min(GENERAL_LEVEL_CAP, nextEarned + 1),
  );
  return {
    ...permanent,
    general: syncGeneralPointsFromLevel({
      ...permanent.general,
      earnedPoints: nextEarned,
      level: nextLevel,
    }),
  };
}

/** Convenience: enough unspent points to open all three specialisations (15). */
export function grantPointsToOpenSpecialisations(
  permanent: PermanentSave,
): PermanentSave {
  const cls = permanent.classes.vanguard;
  const need = Math.max(0, 15 - cls.earnedPoints);
  return need > 0 ? grantVanguardTalentPoints(permanent, need) : permanent;
}
